/**
 * Tipos de etapa: nombre, letra y reglas en un solo lugar para ecommerce y backoffice.
 * Antes cada pantalla escribía a mano sus textos y preguntaba "¿es reimpresión o
 * continuación?"; al agregar un tipo, alguna se quedaba atrás.
 */

/** Tipo de etapa. Espejo de Enumerations.CaseStageType en el backend. */
export enum CaseStageType
{
  NewStage = 1,
  SetupContinuation = 2,
  Reprint = 3,
  LastAlignerExtension = 4
}

/** Etiqueta corta de cada tipo. Vacía para una etapa normal: es la mayoría y no se marca. */
export const CaseStageTypeLabels: { [key: number]: string } = {
  [CaseStageType.NewStage]: '',
  [CaseStageType.SetupContinuation]: 'Continuación',
  [CaseStageType.Reprint]: 'Reimpresión',
  [CaseStageType.LastAlignerExtension]: 'Extensión'
};

/** Nombre completo, para tooltips y filtros. */
export const CaseStageTypeNames: { [key: number]: string } = {
  [CaseStageType.NewStage]: 'Nueva etapa',
  [CaseStageType.SetupContinuation]: 'Continuación de setup',
  [CaseStageType.Reprint]: 'Reimpresión',
  [CaseStageType.LastAlignerExtension]: 'Extensión desde el último alineador'
};

/** Letra del tablero kanban: la tarjeta es angosta. */
export const CaseStageTypeLetters: { [key: number]: string } = {
  [CaseStageType.NewStage]: '',
  [CaseStageType.SetupContinuation]: 'C',
  [CaseStageType.Reprint]: 'R',
  [CaseStageType.LastAlignerExtension]: 'E'
};

/**
 * Tipos que reutilizan un setup ya diseñado y aprobado: de "En revisión" saltan directo a
 * fabricación. Los demás pasan por Setup 1 a 3 y la aprobación del doctor.
 * Espejo de CaseStageTypeRules.SkipsSetupTypes en el backend.
 */
export function skipsSetup(type: CaseStageType): boolean
{
  return type == CaseStageType.SetupContinuation || type == CaseStageType.Reprint;
}

/** Tipo de una etapa. Las anteriores al tipo explícito sólo tienen preSetupUsed: todas eran reimpresiones. */
export function stageTypeOf(stage: any): CaseStageType
{
  return stage?.stageType ?? (stage?.preSetupUsed ? CaseStageType.Reprint : CaseStageType.NewStage);
}

export function stageTypeLabel(stage: any): string
{
  return CaseStageTypeLabels[stageTypeOf(stage)] ?? '';
}

/** Números de alineador que lleva la etapa, por arcada: "superiores 13 al 15 · inferiores 13 al 15". */
export function stageRangeText(stage: any, separator: string = ' · '): string
{
  let tramos = [];

  if (stage?.upperRangeFrom) { tramos.push(`superiores ${stage.upperRangeFrom} al ${stage.upperRangeTo}`); }
  if (stage?.lowerRangeFrom) { tramos.push(`inferiores ${stage.lowerRangeFrom} al ${stage.lowerRangeTo}`); }

  return tramos.join(separator);
}

/**
 * Los números con su contexto: una continuación fabrica un tramo del plan; una extensión
 * lleva alineadores nuevos que siguen la numeración de la etapa anterior.
 */
export function stageRangeSentence(stage: any): string
{
  let range = stageRangeText(stage);
  if (!range) { return ''; }

  return stageTypeOf(stage) == CaseStageType.LastAlignerExtension
    ? `Alineadores ${range}`
    : `Del plan del setup: ${range}`;
}
